import axios from "axios";
import {
  CategorizedKeyExtract,
  AhbExpressionEvaluationResult,
  ContentEvaluationResult,
  PackageKeyConditionExpressionMapping,
} from "ahbicht-ts-models";
import { ServiceBase } from "./ServiceBase";
import {
  EdifactFormat,
  EdifactFormatVersion,
  getCurrentEdifactFormatVersion,
  getEdifactFormatVersionLabel,
  getEdifactFormatVersionValidFrom,
} from "@hochfrequenz/efoli";

export { EdifactFormat, EdifactFormatVersion };

interface ConditionSuccessResponse {
  edifact_format: string;
  condition_key: string;
  condition_text: string;
}

interface ConditionErrorResponse {
  error: string;
  code: number;
}
export default interface ExpressionTree {
  expressionTree: Record<string, unknown>;
}

export interface EdifactFormatVersionData {
  text: string; // e.g. "Oktober 2025"
  value: EdifactFormatVersion | null; // e.g. "FV2510"
  keydate: Date; // midnight Europe/Berlin on the validFrom date
}

export interface EdifactFormatData {
  text: string; // e.g. "UTILMD (Gas)"
  value: EdifactFormat | null;
}

const RECENT_VERSIONS: EdifactFormatVersion[] = [
  EdifactFormatVersion.FV2404,
  EdifactFormatVersion.FV2410,
  EdifactFormatVersion.FV2504,
  EdifactFormatVersion.FV2510,
  EdifactFormatVersion.FV2604,
  EdifactFormatVersion.FV2610,
];

export const AvailableEdifactFormatVersions: EdifactFormatVersionData[] = [
  { text: "Keine Angabe", value: null, keydate: new Date(1990, 0, 1) },
  ...[...RECENT_VERSIONS].reverse().map((v) => {
    const { year, month, day } = getEdifactFormatVersionValidFrom(v);
    return {
      text: getEdifactFormatVersionLabel(v),
      value: v,
      keydate: new Date(year, month - 1, day),
    };
  }),
];

export const AvailableEdifactFormats: EdifactFormatData[] = [
  { text: "Keine Angabe", value: null },
  { text: "APERAK", value: EdifactFormat.APERAK },
  { text: "COMDIS", value: EdifactFormat.COMDIS },
  { text: "CONTRL", value: EdifactFormat.CONTRL },
  { text: "IFTSTA", value: EdifactFormat.IFTSTA },
  { text: "INSRPT", value: EdifactFormat.INSRPT },
  { text: "INVOIC", value: EdifactFormat.INVOIC },
  { text: "MSCONS", value: EdifactFormat.MSCONS },
  { text: "ORDCHG", value: EdifactFormat.ORDCHG },
  { text: "ORDERS", value: EdifactFormat.ORDERS },
  { text: "ORDRSP", value: EdifactFormat.ORDRSP },
  { text: "PARTIN", value: EdifactFormat.PARTIN },
  { text: "PRICAT", value: EdifactFormat.PRICAT },
  { text: "QUOTES", value: EdifactFormat.QUOTES },
  { text: "REMADV", value: EdifactFormat.REMADV },
  { text: "REQOTE", value: EdifactFormat.REQOTE },
  { text: "UTILMD (Gas)", value: EdifactFormat.UTILMDG },
  { text: "UTILMD (Strom)", value: EdifactFormat.UTILMDS },
  { text: "UTILTS", value: EdifactFormat.UTILTS },
];

export default class ExpressionService extends ServiceBase {
  [x: string]: any;

  private errorTolerantAxiosInstance = axios.create({
    validateStatus: function () {
      // This is an axios instance, that does not throw an error if the HTTP status code (in the response header)
      // is negative.
      // This is necessary because (my best guess), since the upgrade of the backend from Azure Function 3 to version 4,
      // the backend returns a negative status code if an error occurs.
      // Before that, it only set the code in the response body but sent a positive status code in the header.
      // This didn't matter in the build which we deployed online, but it makes a huge difference while debugging
      // locally, because any uncaught error blocks the screen from rendering and effectively freezes the app.
      return true;
    },
  });

  public static getCurrentEdifactFormatVersionData(): EdifactFormatVersionData {
    const version = getCurrentEdifactFormatVersion();
    const result = AvailableEdifactFormatVersions.find(
      (item: EdifactFormatVersionData) => item.value === version
    );

    if (!result) {
      throw new Error(
        `Current EDIFACT format version is not available: ${String(version)}`
      );
    }

    return result;
  }

  public async getExpressionTree(
    expression: string,
    format_version: EdifactFormatVersionData,
    format: EdifactFormatData
  ): Promise<ExpressionTree> {
    const effective_format: EdifactFormat | null = format.value ?? null;
    const effective_format_version: EdifactFormatVersion | null =
      format_version.value ?? null;
    try {
      const params: Record<string, string | boolean> = {
        expression,
        concise: true,
      };
      if (effective_format) {
        params.format = effective_format;
      }
      if (effective_format_version) {
        params.format_version = effective_format_version;
      }
      const res = await this.errorTolerantAxiosInstance.get(
        `${this.baseUrl}/ParseExpression`,
        { params }
      );
      res.data = this.removeSingleMarkBeginningNode(res.data);
      return res.data;
    } catch (err) {
      console.error("error: -> ", err);
      throw err;
    }
  }

  //removes the first model mark node (e.g. "Muss" or "Kann") iff there is only one
  public removeSingleMarkBeginningNode(tree: any) {
    if (
      tree["children"] &&
      tree["children"][0]["tree"]["children"] &&
      tree["children"].length == 1
    ) {
      tree["children"] = tree["children"][0]["tree"]["children"];
    }
    return tree;
  }

  public async getConditionText(
    conditionNumber: number,
    edifactFormatVersion: EdifactFormatVersion,
    edifactFormat: EdifactFormat
  ): Promise<string> {
    try {
      const res = await this.errorTolerantAxiosInstance.get<
        ConditionSuccessResponse | ConditionErrorResponse
      >(
        `${this.baseUrl}/ResolveConditionText/${edifactFormatVersion}/${edifactFormat}/${conditionNumber}`
      );

      // Check if response is an error response
      if ("error" in res.data) {
        // Return 'nicht vorhanden' string for 404
        if (res.data.code === 404) {
          return "nicht vorhanden";
        }
        throw new Error(res.data.error);
      }

      return res.data.condition_text || "";
    } catch (err) {
      console.error(
        `Failed to fetch condition text for condition ${conditionNumber}:`,
        err
      );
      return "";
    }
  }

  public async getPackageConditionExpression(
    packageKey: string,
    edifactFormatVersion: EdifactFormatVersion,
    edifactFormat: EdifactFormat
  ): Promise<PackageKeyConditionExpressionMapping> {
    const res = await this.errorTolerantAxiosInstance.get(
      `${this.baseUrl}/ResolvePackageConditionExpression/${edifactFormatVersion}/${edifactFormat}/${packageKey}`
    );
    return res.data;
  }

  public async evaluateCondition(
    contentEvaluationResult: ContentEvaluationResult,
    expression: string,
    format?: EdifactFormat | null,
    formatVersion?: EdifactFormatVersion | null
  ): Promise<AhbExpressionEvaluationResult> {
    try {
      const params: Record<string, string> = { expression };
      if (format) {
        params.format = format;
      }
      if (formatVersion) {
        params.format_version = formatVersion;
      }
      const res = await this.errorTolerantAxiosInstance.post(
        `${this.baseUrl}/ParseExpression`,
        contentEvaluationResult,
        { params }
      );

      return res.data;
    } catch (err) {
      // Handle Error Here
      console.error(err);
      throw err;
    }
  }

  public async getCategorizedKeyExtract(
    expression: string,
    format?: EdifactFormat | null,
    formatVersion?: EdifactFormatVersion | null
  ): Promise<CategorizedKeyExtract> {
    try {
      const params: Record<string, string> = { expression };
      if (format) {
        params.format = format;
      }
      if (formatVersion) {
        params.format_version = formatVersion;
      }
      const res = await this.errorTolerantAxiosInstance.get(
        `${this.baseUrl}/CategorizedKeyExtract`,
        { params }
      );
      return res.data;
    } catch (err) {
      console.error("error:", err);
      throw err;
    }
  }

  // Fetch suggestions from the backend and returns them
  public async fetchSuggestionsFromBackend(
    input: string,
    format_version: EdifactFormatVersionData,
    format: EdifactFormatData
  ): Promise<string[]> {
    if (input) {
      const effective_format: EdifactFormat | null = format.value ?? null;
      const effective_format_version: EdifactFormatVersion | null =
        format_version.value ?? null;
      try {
        const params: Record<string, string> = { expression: input };
        if (effective_format) {
          params.format = effective_format;
        }
        if (effective_format_version) {
          params.format_version = effective_format_version;
        }
        const response = await this.errorTolerantAxiosInstance.get(
          `${this.baseUrl}/SuggestExpression`,
          { params }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        return [];
      }
    } else {
      return [];
    }
  }
}

export class TreeData {
  constructor(
    expression: string = "",
    expressionTree: ExpressionTree = new ExpressionTree(),
    selectedEdifactFormat: EdifactFormatData = AvailableEdifactFormats[0],
    selectedEdifactFormatVersion: EdifactFormatVersionData = AvailableEdifactFormatVersions[0]
  ) {
    this.expression = expression;
    this.expressionTree = expressionTree;
    this.selectedEdifactFormat = selectedEdifactFormat;
    this.selectedEdifactFormatVersion = selectedEdifactFormatVersion;
  }
  public expression;
  public expressionTree;
  public selectedEdifactFormat;
  public selectedEdifactFormatVersion;
}
